// react
import {
	ChangeEvent,
	Dispatch,
	MouseEvent,
	SetStateAction,
	useContext,
	useEffect,
	useState,
} from 'react';

// types
import {
	Act,
	ActPayload,
	Contract,
	Entity,
	ErrorCause,
	Feedback,
	LoadingStatus,
} from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// context
import { ContextBanner } from '../../../App.context';
import { ContextProject } from '../../pages/Project/Project.context';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Alert, AlertTitle, Grid, SelectChangeEvent } from '@mui/material';

// fields
import { Input } from '../../fields/Input/Input';
import { Select } from '../../fields/Select/Select';

// ui
import { Loading } from '../../ui/Loading/Loading';

// utils
import { formatDate } from '../../../utils/dates';
import { getOptionsActStatuses } from '../../../utils/options';

// props
export interface FormEditActProps {
	onClose?: () => void;
	onSuccess?: () => void;
	setStatus: Dispatch<SetStateAction<LoadingStatus>>;
}

export const FormEditAct = ({ onClose, onSuccess, setStatus }: FormEditActProps) => {
	// hooks
	const { t } = useTranslation();

	// state
	const [act, setAct] = useState<Act | null>(null);
	const [contracts, setContracts] = useState<Contract[]>([]);
	const [entities, setEntities] = useState<Entity[]>([]);
	const [feedback, setFeedback] = useState<Feedback | null>(null);
	const [searchParams] = useSearchParams();
	const [values, setValues] = useState<ActPayload>({
		contracts: [],
		dateEnd: '',
		dateStart: '',
		description: '',
		entities: [],
		name: '',
		status: 'STARTED',
	});

	// params
	const paramAct = searchParams.get('dialogAct');

	// context
	const { setBanner } = useContext(ContextBanner);
	const { project } = useContext(ContextProject);

	// vars
	const idForm = 'form-edit-act';

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// fetch act
	const { isLoading: isLoadingAct } = useFetch({
		url: `${process.env.REACT_APP_API_URL}/acts/${paramAct}`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data && res.data.length) {
				setAct(res.data[0]);
			}
		},
	});

	// fetch suppliers
	useFetch({
		isEnabled: Boolean(project?.id),
		url: `${process.env.REACT_APP_API_URL}/entities?project=${project?.id}&type=SUPPLIER&fields=id,name`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (data) => {
			if (data.data) {
				setEntities(data.data);
			}
		},
	});

	// fetch suppliers contracts
	const { fetchRequest: fetchContracts } = useFetch({
		url: `${process.env.REACT_APP_API_URL}/contracts?project=${
			project?.id
		}&entities=${values.entities?.join(',')}&fields=amounts,id,num`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (data) => {
			if (data.data) {
				setContracts(data.data);
			}
		},
	});

	// options
	const optionsContracts = contracts.map((contract) => ({
		label: contract.num,
		secondary:
			contract.amounts.length > 0
				? contract.amounts.map((amount) => amount.currency).join(', ')
				: undefined,
		value: contract.id,
	}));

	const optionsEntities = entities.map((entity) => ({
		label: entity.name,
		value: entity.id,
	}));

	const optionsStatus = getOptionsActStatuses({ hasDescription: true, t });

	const handleSubmit = async (e: MouseEvent<HTMLFormElement>) => {
		e.preventDefault();

		const form = document.querySelector(`#${idForm}`) as HTMLFormElement;
		const fd = new FormData(form);
		const entries = Object.fromEntries(fd.entries());

		try {
			// clear error
			setFeedback(null);

			// set loading
			setStatus('LOADING');

			if (!paramAct) {
				throw new Error('An ID is required', {
					cause: {
						id: 'ID_NON_EXISTENT',
					},
				});
			}

			const body: Partial<ActPayload> = {
				contracts: values.contracts,
				dateEnd: values.dateEnd,
				dateStart: values.dateStart,
				description: values.description,
				entities: values.entities,
				name: String(entries.name),
				status: values.status,
			};

			const fetchEditAct = await fetch(`${process.env.REACT_APP_API_URL}/acts/${paramAct}`, {
				method: 'PATCH',
				body: JSON.stringify(body),
				headers: {
					Authorization: `Bearer ${accessToken}`,
					User: String(idToken),
				},
			});
			const resEditAct = await fetchEditAct.json();

			if (resEditAct.error) {
				throw new Error(resEditAct.error.message, {
					cause: {
						id: resEditAct.error.id,
						details: resEditAct.error?.details,
					},
				});
			}

			if (onSuccess) {
				onSuccess();
			}

			// set success banner
			setBanner({
				hasClose: true,
				message: t('alert.actUpdated', { name: entries.name }),
				severity: 'success',
			});

			// close dialog
			if (onClose) {
				onClose();
			}
		} catch (error) {
			const err = error as Error;
			const cause = err.cause as ErrorCause;

			const feedbackNew = {
				alert: {
					children: t('error.default'),
				},
			};

			if (cause) {
				if (cause.id === 'ACT_UNAUTHORIZED_PATCH') {
					feedbackNew.alert.children = t('error.unauthorized');
				}

				if (cause.id === 'ID_NON_EXISTENT') {
					feedbackNew.alert.children = t('error.idNonExistent');
				}
			}

			// set error
			setFeedback(feedbackNew);
			setStatus('ERROR');
		} finally {
			// set status
			setStatus('DEFAULT');
		}
	};

	const handleChangeValue = (e: ChangeEvent | SelectChangeEvent<unknown>) => {
		const { name, value } = e.target as HTMLInputElement;

		let contractsNew = values.contracts;
		if (name === 'entities' && !value.length) {
			contractsNew = [];
		}

		setValues({
			...values,
			contracts: contractsNew,
			[name]: value,
		});
	};

	// content
	const renderContent = () => {
		if (isLoadingAct) {
			return (
				<Grid item xs={12}>
					<Grid alignItems="center" container justifyContent="center">
						<Grid item>
							<Loading />
						</Grid>
					</Grid>
				</Grid>
			);
		}

		let dateStartMax = null;
		if (values.dateEnd) {
			dateStartMax = new Date(values.dateEnd);
		}

		let dateEndMin = new Date();
		if (values.dateStart) {
			dateEndMin = new Date(values.dateStart);
		}

		return (
			<>
				<Grid item xs={12} md={6}>
					<Input
						id={`${idForm}-name`}
						label={t('name')}
						name="name"
						onChange={handleChangeValue}
						required={true}
						value={values.name}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<Select
						id={`${idForm}-status`}
						label={t('status')}
						name="status"
						onChange={handleChangeValue}
						options={optionsStatus}
						required={true}
						value={values.status}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<Select
						id={`${idForm}-entities`}
						label={t('suppliers')}
						multiple={true}
						name="entities"
						onChange={handleChangeValue}
						options={optionsEntities}
						required={true}
						value={values.entities}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<Select
						disabled={!values.entities?.length}
						helperText={
							values.entities?.length ? undefined : t('formAddTransaction.payment.contract.helper')
						}
						id={`${idForm}-contracts`}
						label={t('contracts')}
						multiple={true}
						name="contracts"
						onChange={handleChangeValue}
						options={optionsContracts}
						required={true}
						value={values.contracts}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<Input
						id={`${idForm}-start`}
						inputProps={{
							// start date must be before end date
							max: dateStartMax ? dateStartMax.toISOString().split('T')[0] : '',
						}}
						label={t('startDate')}
						name="dateStart"
						onChange={handleChangeValue}
						required={true}
						type="date"
						value={values.dateStart}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<Input
						id={`${idForm}-end`}
						inputProps={{
							// end date must be after start date
							min: dateEndMin.toISOString().split('T')[0],
						}}
						label={t('endDate')}
						name="dateEnd"
						onChange={handleChangeValue}
						required={true}
						type="date"
						value={values.dateEnd}
					/>
				</Grid>
				<Grid item xs={12}>
					<Input
						id={`${idForm}-description`}
						label={t('description')}
						minRows={4}
						multiline={true}
						name="description"
						onChange={handleChangeValue}
						value={values.description}
					/>
				</Grid>
				{feedback && (
					<Grid item xs={12}>
						<Alert severity="error" {...feedback.alert}>
							{feedback.title && <AlertTitle {...feedback.title} />}
							{feedback.alert.children}
						</Alert>
					</Grid>
				)}
			</>
		);
	};

	// set default values when act changes
	useEffect(() => {
		if (act) {
			setValues({
				contracts: act.contracts as number[],
				dateEnd: act.dateEnd
					? formatDate({ date: new Date(act.dateEnd), format: 'YYYY-MM-DD' })
					: '',
				dateStart: act.dateStart
					? formatDate({ date: new Date(act.dateStart), format: 'YYYY-MM-DD' })
					: '',
				description: act.description,
				entities: act.entities as number[],
				name: act.name,
				status: act.status,
			});
		}
	}, [act]);

	// fetch contracts when entities change
	useEffect(() => {
		fetchContracts();

		if (!values.entities?.length) {
			setValues((valuesPrev) => {
				return {
					...valuesPrev,
					contracts: [],
				};
			});
		}
	}, [fetchContracts, values.entities]);

	return (
		<form id={`${idForm}`} name="formEditAct" onSubmit={handleSubmit}>
			<Grid container spacing={2}>
				{renderContent()}
			</Grid>
		</form>
	);
};
